<template>
  <artisio-modal
    :visible="showModal"
    :title="computedTitle"
    @hidden="onHidden"
    @show="onShow"
    @ok="onSubmit('preview')"
    @additionalOk="onSubmit('save')"
    :ok-title="$t('Preview and Send Proposal')"
    :additional-ok-title="$t('Save')"
    size="xl"
    :is-loading="isLoading"
    :submit-hint="$t('PDF Document sent to the seller will only include the unsold lots with ‘Seller Confirmed’ (proposal hasn’t been sent) and ‘Suggestion Not Sent’ proposal statuses')">
    <div class="lots-table table-component">
      <artisio-table :items="filteredRows"
                     :fields="tableFields" :enable-simple-search="false"
                     :extra-data="extraData"
                     show-toggle-columns
                     @update-toggle-columns="updateToggleColumns"
                     @on-column-save="updateToggleColumns"
                     enable-front-end-pagination
                     :pagination="pagination"
                     selectable
                     filter-toggle-string="unsold-lots-proposals"
                     :busy="isLoading"
      >
        <template v-slot:header-buttons="{selectedItems}">
          <b-button class="btn-table-actions" variant="primary" @click="onBulkEditClick(selectedItems)"
                    :disabled="selectedItems.size === 0">
            {{ $t('Bulk Edit') }}
          </b-button>
        </template>

        <template v-slot:search-form-placeholder>
          <search-collapse table-id="unsold-lots-proposals">
            <div class="card mb-2">
              <div class="card-body bg-secondary pt-2 pb-0 pl-1 pr-2">
                <b-form class="search-form" @submit.prevent="onSearch">
                  <div class="d-flex">
                    <div class="row align-items-center">
                      <div class="input-wrapper">
                        <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="lot_no"/>
                      </div>
                      <div class="input-wrapper">
                        <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="item_no"/>
                      </div>
                      <div class="input-wrapper">
                        <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="receipt_no"/>
                      </div>
                      <div class="input-wrapper">
                        <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="title"/>
                      </div>
                    </div>
                    <div class="search-btn-wrapper">
                      <b-button variant="primary" @click.prevent="onSearchReset">
                        <font-awesome-icon icon="undo" class="icon"/>
                        {{ $t('Reset') }}
                      </b-button>
                      <b-button type="submit" variant="primary" class="ml-2">
                        <font-awesome-icon icon="search" class="icon"/>
                        {{ $t('Search') }}
                      </b-button>
                    </div>
                  </div>
                </b-form>
              </div>
            </div>
          </search-collapse>
        </template>

        <template slot="cell(lot_no)" slot-scope="{item}">
          {{ item.lot_no ? item.lot_no : '' }}
        </template>

        <template slot="cell(stock_no)" slot-scope="{item}">
          <router-link v-if="$hasPermission('view_receipt')"
                       :to="{name: 'receipt.view', params: {id: item.inventory.receipt_uuid}}"
                       @click.native="onHidden"
          >
            {{ item.inventory.receipt.receipt_no }}
          </router-link>
          /
          <router-link v-if="$hasPermission('view_inventoryitem')"
                       :to="{ name: 'inventory.view', params: { id: item.inventory.uuid }}"
                       @click.native="onHidden"
          >
            {{ item.inventory.item_no }}
          </router-link>

          <action-buttons
            :remove="false"
            :view="false"
            @editClick="onEditClick(item)"/>
        </template>

        <template slot="cell(image)" slot-scope="{item}" class="col-img">
          <b-img v-if="item.inventory.images.length" :src="getImageSrc(item.inventory.images)" style=""/>
          <b-img v-else src="@/assets/no-image.jpg"/>
        </template>

        <template slot="cell(lot_no)" slot-scope="{item}">
          <a v-if="enableContinuousAuction && item.auction && item.auction.live_url && getStatus(item) === 'published'"
             target='_blank' :href="item.auction.live_url">
            {{ item.lot_no ? item.lot_no : '' }}
          </a>
          <span v-else>{{ item.lot_no ? item.lot_no : '' }}</span>
        </template>

        <template slot="cell(title)" slot-scope="{item}">
          <div v-if="item.inventory.is_draft">
            <span class="badge badge-secondary">{{ $t('Draft') }}</span>
          </div>
          {{ item.inventory.short_description | striptags | truncate(50) }}
        </template>

        <template #cell(total_bids)="{item}">
          <span v-if="item.total_bids">
            {{ item.total_bids }} {{ item.total_bids === 1 ? $t('bid') : $t('bids') }}
          </span>
          <span v-else>
            0 {{ $t('bid') }}
          </span>
        </template>

        <template #cell(total_commission_bids)="{item}">
          <span v-if="item.total_commission_bids">
            {{ item.total_commission_bids }} {{ item.total_commission_bids === 1 ? $t('bid') : $t('bids') }}
          </span>
          <span v-else>
            0 {{ $t('bid') }}
          </span>
        </template>

        <template #cell(total_telephone_bids)="{item}">
          <span v-if="item.total_telephone_bids">
            {{ item.total_telephone_bids }} {{ item.total_telephone_bids === 1 ? $t('bid') : $t('bids') }}
          </span>
          <span v-else>
            0 {{ $t('bid') }}
          </span>
        </template>

        <template slot="cell(estimate)" slot-scope="{item}">
          <template v-if="item.low || item.high">
            {{ item.low | asCurrency(currencySymbol) }} -
            {{ item.high | asCurrency(currencySymbol) }}
          </template>
        </template>

        <template slot="cell(start_price)" slot-scope="{item}">
          {{ item.start_price | asCurrency(currencySymbol) }}
        </template>

        <template slot="cell(reserve)" slot-scope="{item}">
          {{ item.reserve | asCurrency(currencySymbol) }}
        </template>

        <template slot="cell(reserve_type)" slot-scope="{item}">
          {{ getReserveType(item.reserve_type) }}
        </template>

        <template slot="cell(new_reserve_type)" slot-scope="{item}">
          <template v-if="getLotInputs(item)">
            {{ getReserveType(getLotInputs(item).new_reserve_type) }}
          </template>
        </template>

        <template slot="cell(suggestion)" slot-scope="{item}">
          <template v-if="getLotInputs(item)">
            {{ getLotInputs(item).proposal_suggestion | proposalStatusSuggestionMap }}
          </template>
        </template>

        <template slot="cell(new_reserve)" slot-scope="{item}">
          <template v-if="getLotInputs(item)">
            {{ getLotInputs(item).new_reserve }}
          </template>
        </template>

        <template slot="cell(new_low)" slot-scope="{item}">
          <template v-if="getLotInputs(item)">
            {{ getLotInputs(item).new_low }}
          </template>
        </template>

        <template slot="cell(new_high)" slot-scope="{item}">
          <template v-if="getLotInputs(item)">
            {{ getLotInputs(item).new_high }}
          </template>
        </template>

        <template slot="cell(new_start_price)" slot-scope="{item}">
          <template v-if="getLotInputs(item)">
            {{ getLotInputs(item).new_start_price }}
          </template>
        </template>

        <template slot="cell(comment)" slot-scope="{item}">
          <template v-if="getLotInputs(item)">
            {{ getLotInputs(item).proposal_comment | truncate(50) }}
          </template>
        </template>

        <template slot="cell(proposal_status)" slot-scope="{item}">
          <template v-if="getLotInputs(item) && getLotInputs(item).proposal_status">
            <span v-as-status="`proposal_${getLotInputs(item).proposal_status}`">
              {{ getLotInputs(item).proposal_status | proposalStatusSuggestionMap }}
            </span>
          </template>
        </template>

        <template slot="cell(proposal_sent_by)" slot-scope="{item}">
          {{ item.reservation.proposal_sent_by ? item.reservation.proposal_sent_by.display_name : '' }}
        </template>

        <template slot="cell(proposal_sent_at)" slot-scope="{item}">
          {{ item.reservation.proposal_sent_at | toDate }}
        </template>

        <template slot="cell(proposal_confirmed_at)" slot-scope="{item}">
          {{ item.reservation.proposal_confirmed_at | toDate }}
        </template>

        <template slot="cell(proposal_confirmed_by)" slot-scope="{item}">
          {{ item.reservation.proposal_confirmed_by ? item.reservation.proposal_confirmed_by.display_name : '' }}
        </template>
      </artisio-table>
    </div>
  </artisio-modal>
</template>

<script>
import {createNamespacedHelpers} from "vuex";
import SearchCollapse from "@/core/components/search-collapse/SearchCollapse.vue";
import ArtisioTable from "@/core/components/ams-table/ArtisioTable.vue";
import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal.vue";
import {VENDORS_TABLE_NAME, VENDORS_UNSOLD_PROPOSALS_TABLE_NAME} from "@/modules/constants";
import cloneDeep from "lodash/cloneDeep";
import ActionButtons from "@/core/components/ams-table/ActionButtons.vue";
import {LotProposalEditModel} from "@/modules/vendors/LotProposalEdit.model";
import InputWidget from "@/core/components/input-widget/InputWidget.vue";
import LotProposalSearchModel from "@/modules/vendors/LotProposalSearch.model";
import SalesService from "@/modules/sales/SalesService";
import EventBus from "@/plugins/event-bus";
import {
  ON_PDF_MODAL_CLOSE,
  ON_PDF_MODAL_OPEN,
  ON_UNSOLD_LOT_PROPOSAL_BULK_INPUT,
  ON_UNSOLD_LOT_PROPOSAL_ERROR,
  ON_UNSOLD_LOT_PROPOSAL_INPUT
} from "@/plugins/event-bus/event-bus-names";
import i18n from "@/shared/i18n";
import striptags from "striptags";

const {mapState, mapActions} = createNamespacedHelpers('vendors/unsoldProposals');
const {mapState: mapStateGlobal} = createNamespacedHelpers('global');

export default {
  name: "ProposalOfUnsoldLotsModal",
  components: {
    InputWidget,
    ActionButtons,
    ArtisioModal,
    ArtisioTable,
    SearchCollapse,
  },
  data: () => {
    return {
      filteredRows: [],
      lots: {},
      searchModel: new LotProposalSearchModel()
    }
  },
  computed: {
    ...mapStateGlobal({
      enableContinuousAuction: state => state.enableContinuousAuction,
      reserveTypes: state => state.reserveTypes,
    }),
    ...mapState({
      showModal: state => state.unsoldProposalModal.show,
      customer: state => state.unsoldProposalModal.customer,
      auction: state => state.unsoldProposalModal.auction,
      toggleColumns: state => state.tableData.toggleColumns,
      tableSource: state => state.tableData.tableSource,
      rows: state => state.tableData.rows,
      fields: state => state.tableData.toggleColumns.fields,
      pagination: state => state.tableData.pagination,
      suggestionOptions: state => state.suggestionOptions,
      proposalStatusOptions: state => state.proposalStatusOptions,
      isLoading: state => state.tableData.loading,
      bulkEditFields: state => state.bulkEditFields
    }),
    computedTitle() {
      if (!this.customer) return ''
      return this.$i18n.t('Proposal of Unsold Lots for {vendor}', {vendor: `${this.customer.first_name}, ${this.customer.last_name}`})
    },
    currencySymbol() {
      if (!this.auction) return ''
      return this.auction.currency.symbol;
    },
    extraData() {
      return {
        ...this.toggleColumns,
        tableName: `${VENDORS_TABLE_NAME}.${VENDORS_UNSOLD_PROPOSALS_TABLE_NAME}.${this.tableSource}`,
      }
    },
    tableFields() {
      return cloneDeep(this.fields).filter(field => field.isVisible)
    },
    activeFilters() {
      return [
        {
          searchAttr: 'lot_no',
          filter: lot => this.searchModel.lot_no === lot.lot_no
        },
        {
          searchAttr: 'item_no',
          filter: lot => this.searchModel.item_no === lot.inventory.item_no
        },
        {
          searchAttr: 'receipt_no',
          filter: lot => lot.inventory.receipt.receipt_no.toLowerCase().includes(this.searchModel.receipt_no.toLowerCase())
        },
        {
          searchAttr: 'title',
          filter: lot => striptags(lot.inventory.short_description).toLowerCase().includes(this.searchModel.title.toLowerCase())
        },
      ].filter(attr => this.searchModel[attr.searchAttr])
    }
  },
  methods: {
    ...mapActions(['hideUnsoldProposalModal', 'updateToggleColumns', 'getTableData', 'showLotEditModal',
      'updateTablePagination', 'showLotBulkEditModal', 'updatePaginationPage']),
    onHidden() {
      this.filteredRows = []
      this.lots = {}
      this.searchModel = new LotProposalSearchModel()
      this.hideUnsoldProposalModal()
    },
    async onShow() {
      await this.getTableData()
      this.initializeData()
    },
    initializeData() {
      this.rows.forEach(lot => {
        this.lots[lot.reservation_uuid] = new LotProposalEditModel({
          new_low: lot.reservation.new_low,
          new_high: lot.reservation.new_high,
          new_reserve: lot.reservation.new_reserve,
          new_start_price: lot.reservation.new_start_price,
          proposal_comment: lot.reservation.proposal_comment,
          proposal_status: lot.reservation.proposal_status,
          proposal_suggestion: lot.reservation.proposal_suggestion,
          new_reserve_type: lot.reservation.new_reserve_type,
        })
      })
      this.filteredRows = this.rows
      this.updateTablePagination({total: this.filteredRows.length})
    },
    getLotInputs(id, nested = true) {
      if (nested) {
        id = id.reservation_uuid
      }
      return this.lots[id]
    },
    setLotInputs(payload) {
      this.lots = {...this.lots, ...payload}
    },
    async onEditClick(lot) {
      await this.showLotEditModal({
        lot: this.getLotInputs(lot),
        lot_no: lot.lot_no,
        reservation_uuid: lot.reservation_uuid
      })
    },
    async onSearch() {
      await this.updatePaginationPage(1);
      this.filteredRows = this.rows.filter(lot => this.activeFilters.every(filterObj => filterObj.filter(lot)))
    },
    async onSearchReset() {
      await this.updatePaginationPage(1);
      this.filteredRows = this.rows
      this.searchModel = new LotProposalSearchModel()
      this.getTableData()
    },
    async onSubmit(action) {
      SalesService.sendProposalOfUnsoldLotsNotification({
        auction_uuid: this.auction.uuid,
        customer_uuid: this.customer.customer_uuid,
        data: {reservations: this.lots, action: action}
      }).then(res => {
        if (res.success) {
          if (action === 'preview') {
            if (!res.body || !res.body.attachment) {
              this.$errorDialog(this.$i18n.t("To preview the PDF, the selected lots must have a proposal status ‘Seller Confirmed’ (proposal hasn't been sent) or ‘Suggestion Not Sent’."));
            } else {
              EventBus.$emit(ON_PDF_MODAL_OPEN, {
                title: i18n.t(`Preview of proposal of unsold lots PDF`),
                data: {
                  pdf_url: res.body.attachment
                },
                actions: [
                  {
                    display_name: i18n.t('Confirm And Send Notification'),
                    handler: () => {
                      this.$confirm(this.$i18n.t('Are you sure you want to send proposal of unsold lots to following customer?')).then(
                        confirmed => {
                          if (!confirmed) {
                            return;
                          }
                          SalesService.sendProposalOfUnsoldLotsNotification({
                            auction_uuid: this.auction.uuid,
                            customer_uuid: this.customer.customer_uuid,
                            data: {reservations: this.lots, action: 'send_notification'}
                          }).then(res => {
                            if (res.success) {
                              this.onHidden()
                              this.$toast('success', this.$i18n.t("Proposal of unsold lots was successfully sent"));
                            }
                            EventBus.$emit(ON_PDF_MODAL_CLOSE);
                          });
                        })
                    }
                  },
                  {
                    display_name: i18n.t('Cancel'),
                    handler: () => EventBus.$emit(ON_PDF_MODAL_CLOSE)
                  }]
              });
            }
          } else {
            this.onHidden()
            this.$toast('success', this.$i18n.t('Proposal of unsold lots was successfully saved'));
          }
        } else {
          if (res.body.non_field_errors) {
            const listItemsArr = [];
            res.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
          }
        }
      });
    },
    onBulkEditClick(selectedItems) {
      this.showLotBulkEditModal([...selectedItems].map(item => item.reservation_uuid))
    },
    onBulkEditInput(data) {
      const {emptyModel, presetModel, reservation_uuids} = data
      const presetFields = []
      const emptyFields = []

      this.bulkEditFields.forEach(field => {
        if (emptyModel[field]) {
          emptyFields.push(field)
        }
        if (presetModel[field]) {
          presetFields.push(field)
        }
      })
      reservation_uuids.forEach(reservation_uuid => {
          const lot = this.getLotInputs(reservation_uuid, false)
          emptyFields.forEach(field => {
            lot[field] = null
          })
          presetFields.forEach(field => {
            lot[field] = presetModel[field]
          })
          this.setLotInputs({[reservation_uuid]: lot})
        }
      )
    },
    getImageSrc(images) {
      for (let img of images) {
        if (img.data && (img.data.sm || img.data.original)) {
          return img.data.sm ? img.data.sm.url : img.data.original.url;
        }
      }
      return null;
    },
    getReserveType(reserveType) {
      const type = this.reserveTypes.find(type => type.value === reserveType)
      return type ? type.key : ""
    },
  },
  watch: {
    filteredRows() {
      this.updateTablePagination({total: this.filteredRows.length})
    }
  },
  beforeMount() {
    this.$evBus.$on(ON_UNSOLD_LOT_PROPOSAL_INPUT, data => this.setLotInputs(data));
    this.$evBus.$on(ON_UNSOLD_LOT_PROPOSAL_ERROR, data => this.$errorDialog(data));
    this.$evBus.$on(ON_UNSOLD_LOT_PROPOSAL_BULK_INPUT, data => this.onBulkEditInput(data));
  },
  beforeDestroy() {
    this.$evBus.$off(ON_UNSOLD_LOT_PROPOSAL_INPUT);
    this.$evBus.$off(ON_UNSOLD_LOT_PROPOSAL_ERROR);
    this.$evBus.$off(ON_UNSOLD_LOT_PROPOSAL_BULK_INPUT);
  }
}
</script>

<style>
.img-holder {
  > img {
    width: 100px;
  }
}
</style>


